import { createError, useAsyncData, useNuxtApp, useRoute } from '#imports'
import { type Ref, computed } from 'vue'

import {
  type HttpRequestUrlParameters,
  productReviewsAPI,
} from '@backmarket/http-api'
import { getBreadcrumb } from '@backmarket/http-api/src/api-specs-navigation-experience/product/breadcrumb'
import {
  getPickers,
  getServicesPickers,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import {
  type GetProductResponse,
  getProduct,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { getTechnicalSpecifications } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import { filterSelectedOfferABTestedPaymentMethods } from '@backmarket/nuxt-layer-payment/methods/helpers/filterABTestedPaymentMethods'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { useSkinnyBannerLoader } from '~/scopes/branding/components/SkinnyBanner/useSkinnyBanner'
import { useFetchFormattedCMSSections } from '~/scopes/cms/composables/useFetchFormattedCMSSections'
import { useFetchReviewInCarousel } from '~/scopes/product/components/GalleryNew/composables/useFetchReviewInCarousel'
import { useReviewsAISummary } from '~/scopes/reviews/ai-summary/composables/useReviewsAISummary'
import { useRatingDetailsSummary } from '~/scopes/reviews/reviews-display/composables/useRatingDetailsSummary'
import { MAX_REVIEWS_DISPLAYED_IN_LIST } from '~/scopes/reviews/reviews-display/constants'
import { transformReviewsResponse } from '~/scopes/reviews/reviews-display/utils/transformReviewsResponse'

import { getOptionalEndpointTimeoutOptions } from '../utils/optionalEndpointTimeout'

import { useMasterPPScale } from './useMasterPPScale/useMasterPPScale'

export async function useProductRequests(
  productId: string,
  grade: Ref<number | null>,
  offerType: Ref<number | null>,
  mobilePlan: Ref<string | null>,
) {
  const optionalEndpointOptions = getOptionalEndpointTimeoutOptions()

  const route = useRoute()
  const experiments = useExperiments()
  const locale = useI18nLocale()
  const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
  const nuxtApp = useNuxtApp()

  const fetchReviewInCarousel = useFetchReviewInCarousel()

  const { isMasterPPScaleUrl } = useMasterPPScale()

  let pickersResponseCacheTagHeader = ''

  const requestParams = { pathParams: { productId } }

  const { getRating, getReviews } = productReviewsAPI

  const pickersQueryParams = computed(() => {
    const values = {
      grade: grade.value ? String(grade.value) : null,
      special_offer_type: offerType.value ? String(offerType.value) : null,
      premium_grade: true,
      cheapest_by_grade: !route.query.variantClicked,
    }

    return removeEmptyValuesInObject(values) as HttpRequestUrlParameters
  })

  const servicePickersQueryParams = computed(() => {
    return removeEmptyValuesInObject({
      mobile_plan_offer_id: mobilePlan.value ? String(mobilePlan.value) : null,
    })
  })

  const reviewsQueryParams = computed(() => ({
    ...route.query,
    page_size:
      experiments['experiment.reviewAmount'] === 'tenReviews'
        ? 10
        : MAX_REVIEWS_DISPLAYED_IN_LIST,
    translation_locale: locale,
    order_by: route.query.order_by ?? '-relevance_alt',
  }))

  const [
    {
      data: productAndReviewInCarouselResponse,
      error: productAndReviewInCarouselError,
    },
    {
      data: servicesPickersResponse,
      pending: isServicesPickersResponsePending,
      refresh: callServicesPickers,
    },
    { data: breadcrumbResponse },
    { data: technicalSpecificationsResponse },
    {
      data: pickersResponse,
      pending: isPickersResponsePending,
      error: pickersError,
    },
    { data: ratesResponse },
    { data: reviewsResponse, pending: isReviewsResponsePending },
    { pending: reviewsSummaryPending, data: reviewsSummaryResponse },
    { data: reviewsAISummary, pending: reviewsAiSummaryPending },
    { data: cmsSections },
  ] = await Promise.all([
    useAsyncData(
      `product-and-review-in-carousel-${productId}`,
      async () => {
        try {
          const transformProduct = (p: GetProductResponse) => {
            if (isMasterPPScaleUrl.value) {
              return {
                ...p,
                titles: {
                  ...p.titles,
                  raw: p.titles.default,
                },
              }
            }

            return p
          }

          const productRes = await $httpFetch(getProduct, {
            pathParams: { productId },
            queryParams: {
              withVisiblePartnership: true,
            },
          }).catch(() => null)

          // If product fetch failed, return early with undefined values
          if (!productRes) {
            return { product: undefined, reviewInCarousel: undefined }
          }

          const product = transformProduct(productRes)

          /**
           * Use `runWithContext` to preserve Nuxt context when calling composables
           * inside an async callback. This is necessary because the context is lost
           * after awaiting operations in async functions.
           *
           * @see https://nuxt.com/docs/api/composables/use-nuxt-app#runwithcontext
           */
          const reviewInCarousel = await nuxtApp
            .runWithContext(async () => {
              return fetchReviewInCarousel({
                categoryId: product.tracking.categoryId,
                productBrand: product.brand,
                productModel: product.model ?? '',
                fetchOptions: {
                  ...optionalEndpointOptions,
                },
              })
            })
            .catch(() => undefined)

          return { product, reviewInCarousel }
        } catch {
          // Double safety: catch any unexpected errors
          // Return undefined values instead of throwing to prevent 500 errors
          return { product: null, reviewInCarousel: undefined }
        }
      },
      {
        default: () => ({ product: null, reviewInCarousel: undefined }),
      },
    ),
    useHttpFetch(getServicesPickers, {
      pathParams: { productId },
      queryParams: servicePickersQueryParams,
      // Services pickers currently returns mobile plan & trade-in pickers. They're only called
      // client-side as they rely on user's session (especially the trade-in one, that checks the user's cart),
      // meaning that we can't cache their response.
      server: false,
      // Use immediate:false and watch:false to activate the kill switch on this endpoint.
      lazy: true,
    }),
    // Optional calls
    useHttpFetch(getBreadcrumb, {
      ...requestParams,
      ...optionalEndpointOptions,
    }),
    useHttpFetch(getTechnicalSpecifications, {
      ...requestParams,
      ...optionalEndpointOptions,
      lazy: true,
    }),
    useHttpFetch(getPickers, {
      pathParams: { productId },
      queryParams: pickersQueryParams,
      onEvent: (_e, context) => {
        pickersResponseCacheTagHeader =
          context.response?.headers.get('Cache-Tag') ?? ''
      },
    }),
    useHttpFetch(getRating, {
      pathParams: { uuid: productId },
      ...optionalEndpointOptions,
    }),
    useHttpFetch(getReviews, {
      headers: { 'BM-Page-Name': route.name },
      pathParams: { uuid: productId },
      queryParams: reviewsQueryParams,
      transform: ({ results, previousCursor, nextCursor }) => ({
        results: transformReviewsResponse(results),
        previousCursor,
        nextCursor,
      }),
      // Timeout reduced from 5s to 1s to mitigate 500 errors on product pages likely due to HTTP attacks.
      // Incident & investigation ongoing.
      timeout: 1000,
      lazy: true,
    }),
    useRatingDetailsSummary(),
    useReviewsAISummary(),
    useFetchFormattedCMSSections({ timeout: 2000 }),
    loadSkinnyBannerData(),
  ])

  const productResponse = computed(
    () => productAndReviewInCarouselResponse.value?.product ?? null,
  )
  const reviewInCarouselResponse = computed(
    () => productAndReviewInCarouselResponse.value?.reviewInCarousel,
  )

  // Those 2 endpoints are the only one required to display a viable product page, allowing the user to add a product to its cart,
  // so that's the only 2 that should trigger an error page (404 or 500)
  if (productAndReviewInCarouselError.value || pickersError.value) {
    throw createError({
      statusCode:
        productAndReviewInCarouselError.value?.statusCode ||
        pickersError.value?.statusCode ||
        // added as fallback: in case we don't have product and pickers
        // responses, we should trigger an error page with a fatal error
        500,
      statusMessage: '[PRODUCT] not found',
      cause:
        JSON.stringify(
          productAndReviewInCarouselError.value?.cause ||
            pickersError.value?.cause,
        ) ||
        productAndReviewInCarouselError.value?.statusMessage ||
        pickersError.value?.statusMessage ||
        'Unknown cause',
      // https://nuxt.com/docs/getting-started/error-handling#createerror
      // If you throw an error created with createError:
      // - on server-side, it will trigger a full-screen error page which you can clear with clearError.
      // - on client-side, it will throw a non-fatal error for you to handle. If you need to trigger a full-screen error page, then you can do this by setting fatal: true.
      fatal: true,
    })
  }

  const isOutOfStock = computed(() => {
    if (!pickersResponse.value && !isPickersResponsePending.value) {
      return true
    }

    return pickersResponse.value?.isOutOfStock ?? false
  })

  const selectedOffer = computed(() => {
    if (!pickersResponse.value?.selectedOffer || isOutOfStock.value) {
      return null
    }

    return filterSelectedOfferABTestedPaymentMethods(
      experiments,
      pickersResponse.value?.selectedOffer,
    )
  })

  return {
    productResponse,
    reviewInCarouselResponse,
    breadcrumbResponse,
    technicalSpecificationsResponse,
    pickersResponseCacheTagHeader,
    pickersResponse,
    servicesPickersResponse,
    isPickersResponsePending,
    isServicesPickersResponsePending,
    ratesResponse,
    reviewsResponse,
    isReviewsResponsePending,
    isOutOfStock,
    selectedOffer,
    callServicesPickers,
    cmsSections,
    reviewsSummaryPending,
    reviewsSummaryResponse,
    reviewsAISummary,
    reviewsAiSummaryPending,
  }
}
